import React, { useState, Suspense } from 'react';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import { RootState } from 'handlers';
import { uploadStudentAidData } from 'thunks';
import { FlowComponentType } from 'routes/types';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import DropzoneWidget, { useFiles } from 'components/DropzoneWidget';
import { getApplicationData } from 'selectors/getApplicationData';
import { getStudentLoanApplication } from 'selectors/getStudentLoanApplication';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import { ReactComponent as CheckIcon } from 'images/green-check-rounded.svg';
import CardSequence from 'components/Common/CardSequence';
import { UploadLoanInformationResult } from 'enums/StudentLoanForgivenessFlowResults';
import { ButtonType } from 'components/Button/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import { InfoNote } from 'components/StudentLoanForgiveness/Common/ResultBlocks';

import styles from './UploadLoanInformation.module.scss';

const FilesPreview = React.lazy(() => import('components/FilesPreview'));

/** The studentaid.gov "Download My Aid Data" export is plain text. JSON is not that file. */
export const STUDENT_AID_UPLOAD_EXTENSIONS = ['text/plain'];

const UPLOAD_ERROR_TITLE = "We couldn't upload that file.";
const UPLOAD_ERROR_BODY = 'Download My Aid Data again from studentaid.gov and upload the new file.';

const renameFiles = (cb: (files: File[]) => void, filePrefix?: string) => (files: File[]) => {
  const prefix = filePrefix ? `${filePrefix} ` : '';
  return cb(files.map((file) => new File([file], `${prefix}${file.name}`, { type: file.type })));
};

const UploadLoanInformation = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { handleAddFiles, files, handleRemoveFiles } = useFiles();
  const [hasUploadError, setHasUploadError] = useState(false);

  const {
    applicationId,
    applicationData: { applyProgress },
  } = useSelector(getStudentLoanApplication);
  const { result } = useSelector(getStudentLoanData);

  const { isApplicationStatusUpdating } = useSelector(getApplicationData);
  const { isLoading } = useSelector((state: RootState) => state.filesUpload);

  const handleContinue = async () => {
    const filesForUpload: File[] = files.map((item) => item.file);

    analytics.track('Upload Loan Information in Progress');
    setHasUploadError(false);
    try {
      const { result: uploadedResult } = await dispatchWithUnwrap(
        uploadStudentAidData({
          applicationId: applicationId!,
          file: filesForUpload[0],
        }),
      );
      handleNext(uploadedResult.stage);
    } catch (error: unknown) {
      setHasUploadError(true);
    }
  };

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <FormContainer
          title="Refine your estimate"
          subtitle="Your studentaid.gov file shows your loan types, interest rates, current plan and PSLF count."
          className={styles.formContainer}
        >
          <p>Please follow these steps:</p>

          <CardSequence
            steps={[
              {
                title: (
                  <>
                    1. Log in to{' '}
                    <a href="https://studentaid.gov/fsa-id/sign-in" target="_blank" rel="noreferrer">
                      studentaid.gov
                    </a>{' '}
                    with your FSA ID.
                  </>
                ),
              },
              {
                title: '2. View Details',
                body: (
                  <>
                    On your dashboard, select <b>View Details</b>. The screen will refresh.
                    <img src="/student-aid-images/image1.png" alt="Example" width="100%" />
                  </>
                ),
              },
              {
                title: '3. Download My Aid Data',
                body: (
                  <>
                    Select <b>Download My Aid Data</b> and upload the new file here.
                    <img src="/student-aid-images/image2.png" alt="Example" width="100%" />
                  </>
                ),
              },
            ]}
          />

          <p>Upload the file below:</p>

          <DropzoneWidget
            handleFiles={renameFiles(handleAddFiles, 'StudentAidData')}
            extensions={STUDENT_AID_UPLOAD_EXTENSIONS}
            multiple={false}
          />

          {files.length ? (
            <Suspense fallback={<ReactLoading type="spin" color="#fff" />}>
              <FilesPreview files={files} handleRemove={handleRemoveFiles} />
            </Suspense>
          ) : (
            <></>
          )}

          {hasUploadError && <InfoNote title={UPLOAD_ERROR_TITLE} body={UPLOAD_ERROR_BODY} />}

          {applyProgress?.loanInformation && (
            <>
              <div className={styles.uploaded}>
                <CheckIcon className={styles.checkIcon} />
                <p className={styles.verifiedLabel}>Loan information uploaded</p>
              </div>

              {!files.length && result && <Button onClick={() => handleNext(result.stage)}>Next</Button>}
            </>
          )}

          {Boolean(!applyProgress?.loanInformation || files.length) && (
            <div className={styles.buttonContainer}>
              <Button
                disabled={!files.length}
                className={styles.button}
                onClick={handleContinue}
                isLoading={isLoading || isApplicationStatusUpdating}
              >
                Show my plan
              </Button>
              <Button type={ButtonType.Transparent} onClick={() => handleNext(UploadLoanInformationResult.NotNow)}>
                Not now
              </Button>
            </div>
          )}
        </FormContainer>
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default UploadLoanInformation;
